// Google sign-in WITHOUT Google Identity Services (copied from the admin
// panel, task #513 — see task #565).
//
// GSI's rendered button (`google.accounts.id`) resolves the user's Google
// session through accounts.google.com in a third-party context. Once Chrome
// restricts third-party cookies for a profile, the click is swallowed in
// silence — no popup, no navigator.credentials.get, nothing in the console.
// That is why sign-in kept working on localhost (exempt from the restriction)
// and was dead on the deployed portal domains. Switching the button to FedCM
// (`use_fedcm_for_button`) did not bring it back either.
//
// So we drive the flow ourselves: a real button opens Google's OpenID Connect
// authorization endpoint in a popup, Google redirects back to
// /google-callback on OUR origin with the ID token in the URL fragment, and
// that page relays the token to the window that started the flow. A popup is a
// first-party context for accounts.google.com, so nothing here depends on
// third-party cookies, on FedCM, or on a script we don't control.
//
// The result is the same Google ID token the API already expects
// (`POST /auth/google { idToken }`), so the backend is untouched.

export const GOOGLE_CLIENT_ID =
  '319392197931-l95skv92a101qtn27cuuthtbpvihu0cb.apps.googleusercontent.com';

// Must match, character for character, an "Authorized redirect URI" on the
// OAuth client in Google Cloud Console — one per origin this portal is served
// from (prod domain, dev domain, http://localhost:3000).
export const GOOGLE_CALLBACK_PATH = '/google-callback';

// Where the popup-blocked fallback bounces back to: this portal's login screen
// (the admin panel serves its login at `/`, we serve ours at `/login`).
export const LOGIN_PATH = '/login';

const AUTH_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

// Tag on every message the callback page relays, so a stray postMessage from
// something else on the origin can't be mistaken for a sign-in result.
export const GOOGLE_MESSAGE_SOURCE = 'outfino-google-auth';
const CHANNEL_NAME = 'outfino-google-auth';

// One-time values proving a response belongs to a click that happened in THIS
// tab. sessionStorage is per-tab and the popup is a separate tab, so only the
// window that started the flow can validate — which is exactly the split we
// want: the popup relays, the opener verifies.
const STATE_KEY = 'outfino_google_state';
const NONCE_KEY = 'outfino_google_nonce';
// Only used by the popup-blocked fallback, where the whole tab leaves for
// Google and the login screen's own query string would otherwise be lost on
// the way back.
const RETURN_SEARCH_KEY = 'outfino_google_return_search';

function randomToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function googleRedirectUri() {
  return `${window.location.origin}${GOOGLE_CALLBACK_PATH}`;
}

// Builds the authorization URL and remembers the state/nonce it is tied to.
export function buildGoogleAuthUrl() {
  const state = randomToken();
  const nonce = randomToken();
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    // Implicit ID-token flow: Google returns a signed JWT in the fragment and
    // no access token, so there is nothing to exchange server-side and no
    // client secret to deploy. The fragment never reaches a server — the token
    // only leaves the browser as the `idToken` we post to our own API.
    response_type: 'id_token',
    scope: 'openid email profile',
    redirect_uri: googleRedirectUri(),
    state,
    nonce,
    // People routinely have several Google accounts; always let them pick.
    prompt: 'select_account',
  });
  return `${AUTH_ENDPOINT}?${params.toString()}`;
}

// Reads what Google sent back. Success lands in the fragment; the error case
// can arrive either way, so check both.
export function parseGoogleCallbackParams(loc = window.location) {
  const hash = new URLSearchParams(loc.hash.replace(/^#/, ''));
  const query = new URLSearchParams(loc.search);
  const get = (key) => hash.get(key) ?? query.get(key);
  return { idToken: get('id_token'), state: get('state'), error: get('error') };
}

export function hasGoogleCallbackParams(loc = window.location) {
  const params = parseGoogleCallbackParams(loc);
  return !!(params.idToken || params.error);
}

function decodeJwtPayload(jwt) {
  try {
    const raw = jwt.split('.')[1];
    if (!raw) return null;
    const json = atob(raw.replace(/-/g, '+').replace(/_/g, '/'));
    // The payload is UTF-8; atob yields latin-1, so names with accents would
    // decode to mojibake. We only read `nonce` here, but decode properly
    // anyway so a caller can trust every claim it sees.
    const bytes = Uint8Array.from(json, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

// Validates a callback against the state/nonce this tab generated and returns
// the ID token, or null if anything is off. Deliberately total (no throwing):
// a stray or replayed fragment must never turn into a login attempt. The
// one-time values are consumed either way.
export function verifyGoogleCallback(params) {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  if (!params?.idToken || !expectedState || params.state !== expectedState)
    return null;
  const payload = decodeJwtPayload(params.idToken);
  // Signature, audience and expiry are verified by the API with
  // google-auth-library; the nonce is ours alone to check, and it is what ties
  // this token to the click that started the flow.
  if (!payload || payload.nonce !== expectedNonce) return null;
  return params.idToken;
}

// Opens the consent popup, roughly centred on the current window. Returns null
// when the browser blocked it, which is the caller's cue to fall back to a
// same-tab redirect.
export function openGooglePopup(url) {
  const width = 500;
  const height = 640;
  const left = Math.max(
    0,
    Math.round(window.screenX + (window.outerWidth - width) / 2)
  );
  const top = Math.max(
    0,
    Math.round(window.screenY + (window.outerHeight - height) / 2)
  );
  return window.open(
    url,
    'outfino-google-login',
    `popup,width=${width},height=${height},left=${left},top=${top}`
  );
}

// Channel between the callback page and the window that started the flow.
// BroadcastChannel rather than `window.opener.postMessage` on purpose:
// accounts.google.com already sends Cross-Origin-Opener-Policy in report-only
// mode, and the day Google enforces it the opener link is severed and a
// postMessage relay silently stops working. A same-origin broadcast doesn't
// care how the windows are related. `window.opener` stays as the fallback for
// browsers without BroadcastChannel.
export function openGoogleChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  return new BroadcastChannel(CHANNEL_NAME);
}

export function rememberReturnSearch(search) {
  if (search) sessionStorage.setItem(RETURN_SEARCH_KEY, search);
  else sessionStorage.removeItem(RETURN_SEARCH_KEY);
}

export function takeReturnSearch() {
  const search = sessionStorage.getItem(RETURN_SEARCH_KEY) || '';
  sessionStorage.removeItem(RETURN_SEARCH_KEY);
  return search;
}
